import { on } from '@ember/modifier'
import { hash } from '@ember/helper'
import type { TOC } from '@ember/component/template-only'
import Component from '@glimmer/component'
import { LinkTo } from '@ember/routing'

interface Signature {
  Element: HTMLElement
  Args: {
    isActive?: boolean
    onClick?: () => unknown
    query?: Record<string, unknown>
    route?: string
    iconOnly?: boolean
    isDisabled?: boolean
  }
  Blocks: {
    default: []
  }
}

export const BasePageSelectorTraverser: TOC<Signature> = <template>
  {{#if @isDisabled}}
    {{! Disabled Pagination }}
    <BasePageButton @iconOnly={{@iconOnly}} disabled={{true}} ...attributes>
      {{yield}}
    </BasePageButton>
  {{else if @onClick}}
    {{! Action based Pagination (without Query Params) }}
    <BasePageButton @isActive={{@isActive}} @iconOnly={{@iconOnly}} {{on "click" @onClick}} ...attributes>
      {{yield}}
    </BasePageButton>
  {{else}}
    {{! Query Param based Pagination }}
    <BasePageLinkToButton
      @isActive={{@isActive}}
      @iconOnly={{@iconOnly}}
      @route={{@route}}
      @query={{if @query @query (hash)}}
      ...attributes
    >
      {{yield}}
    </BasePageLinkToButton>
  {{/if}}
</template>

const baseClasses =
  'inline-flex items-center gap-x-2 whitespace-nowrap py-2 rounded-lg border border-transparent bg-transparent text-sm font-medium text-neutral-300 transition-colors duration-300 ease-in-out hover:bg-neutral-75 disabled:hover:bg-transparent focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-325 active:bg-neutral-100 active:text-neutral-400 disabled:text-neutral-200 selected:bg-blue-100 selected:text-blue-350'

interface BasePageButtonSignature {
  Element: HTMLButtonElement
  Args: {
    isActive?: boolean
    iconOnly?: boolean
  }
  Blocks: {
    default: []
  }
}

export class BasePageButton extends Component<BasePageButtonSignature> {
  get baseAndPxClasses() {
    return `${baseClasses} ${this.args.iconOnly ? 'px-2' : 'px-4'}`
  }

  <template>
    <button class="{{if @isActive 'active'}} {{this.baseAndPxClasses}}" type="button" ...attributes>
      {{yield}}
    </button>
  </template>
}

interface BasePageLinkToButtonSignature {
  Element: HTMLAnchorElement
  Args: {
    isActive?: boolean
    route?: string
    query?: Record<string, unknown>
    iconOnly?: boolean
  }
  Blocks: {
    default: []
  }
}

export class BasePageLinkToButton extends Component<BasePageLinkToButtonSignature> {
  get baseAndPxClasses() {
    return `${baseClasses} ${this.args.iconOnly ? 'px-2' : 'px-4'}`
  }

  <template>
    <LinkTo
      {{! LinkTo normally applies activeClass when it is determined to be the currentRoute. That is difficult to test as it
      relies on routing being setup in order to determine its active state. Instead, we can do the functional equivalent
      and apply the active class ourselves when currentPage === pageNumber. }}
      class="{{if @isActive 'active'}} {{this.baseAndPxClasses}}"
      @route={{@route}}
      @query={{if @query @query (hash)}}
      {{! In theory, this should work just fine without current-when (and it seems to in most cases). However, when
      transitioning between some routes, particularly to one with an async model hook, the LinkTo "isActive" check
      fails with a shouldSupersede error. Figuring this is another ember routing / query param bug. We can skip its
      internal isActive check by specifying current-when arg}}
      @current-when={{if @isActive true false}}
      ...attributes
    >
      {{yield}}
    </LinkTo>
  </template>
}
